/**
 * The workshops referential. Admin only, no store filter, no MTM twin.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { useFillerRows } from '@/shared/ui/useFillerRows';
import plusIcon from '@/assets/images/plus.svg';
import { workshopRecordsQueryOptions, type WorkshopRecord } from './api';
import { WorkshopDialog } from './WorkshopDialog';

const COLUMNS = [10, 15, 30, 25, 10, 10];

export function WorkshopsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate('/main');
  }, [user, isAdmin]);

  const workshops = useQuery({
    ...workshopRecordsQueryOptions(),
    enabled: isAdmin,
  });
  const [dialog, setDialog] = useState<{ item: WorkshopRecord | null } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const items = workshops.data ?? [];
  const fillerRows = useFillerRows(
    containerRef,
    'tr.workshop-row',
    items.length,
  );

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialog(null);
    if (result === 'Confirm') {
      void queryClient.invalidateQueries({
        queryKey: workshopRecordsQueryOptions().queryKey,
      });
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu />
      <div className="summary-table my-12">
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-workshop"
          onClick={() => setDialog({ item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a workshop</span>
        </button>
        <div className="table-container">
          <div
            className="items-container"
            ref={containerRef}
            role="region"
            aria-label="Workshops"
          >
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '10%' }}>WorkshopID</th>
                  <th style={{ width: '15%' }}>Name</th>
                  <th style={{ width: '30%' }}>Full address</th>
                  <th style={{ width: '25%' }}>Email</th>
                  <th style={{ width: '10%' }}>phone</th>
                  <th style={{ width: '10%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="workshop-row">
                    <td style={{ width: '10%' }} data-testid="workshop-row-id">
                      <span>W{item.id}</span>
                    </td>
                    <td
                      style={{ width: '15%' }}
                      title={item.name ?? ''}
                      data-testid="workshop-row-name"
                    >
                      {item.name}
                    </td>
                    <td
                      style={{ width: '30%' }}
                      title={item.fullAddress ?? ''}
                      data-testid="workshop-row-full-address"
                    >
                      {item.fullAddress}
                    </td>
                    <td
                      style={{ width: '25%' }}
                      title={item.email ?? ''}
                      data-testid="workshop-row-email"
                    >
                      {item.email}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={item.phone ?? ''}
                      data-testid="workshop-row-phone"
                    >
                      {item.phone}
                    </td>
                    <td style={{ width: '10%' }} className="edit-action">
                      <button
                        type="button"
                        className="button"
                        onClick={() => setDialog({ item })}
                        data-testid="workshop-row-edit-button"
                      >
                        edit
                      </button>
                    </td>
                  </tr>
                ))}
                {Array.from({ length: fillerRows }, (_, index) => (
                  <tr key={`empty-${index}`} className="empty-row">
                    {COLUMNS.map((width, cell) => (
                      <td key={cell} style={{ width: `${width}%` }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {dialog && (
        <WorkshopDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
